import { isNil } from 'es-toolkit';
import { LOCAL_STORAGE_KEYS } from 'src/constants/local-storage';

import defaultGuard from '@savvy/utils/default-guard';
import { jsonParse } from '@savvy/utils/error-handling/json-parse';

import type { SortBy } from './tax-overview-url-state';

export type TaxOverviewTableColumn = 'Client' | 'Filing Type' | 'Tax Year' | 'Tax Status' | 'Tax Price';

const STORAGE_KEY = LOCAL_STORAGE_KEYS.DEFAULT_TAX_OVERVIEW_COLUMNS;

const ALL_TABLE_COLUMNS: readonly TaxOverviewTableColumn[] = [
  'Client',
  'Filing Type',
  'Tax Year',
  'Tax Status',
  'Tax Price',
] as const;

const ALL_TABLE_COLUMNS_SET = new Set<string>(ALL_TABLE_COLUMNS);

export const REQUIRED_COLUMN: TaxOverviewTableColumn = 'Client';

export const DEFAULT_TABLE_COLUMNS: TaxOverviewTableColumn[] = ['Filing Type', 'Tax Status', 'Tax Price'] as const;

type ColumnSizing = {
  flex: string;
  minWidth: number;
};

export function getDefaultColumns(): TaxOverviewTableColumn[] {
  if (typeof window === 'undefined') {
    return DEFAULT_TABLE_COLUMNS;
  }

  try {
    const userDefaultColumns = localStorage.getItem(STORAGE_KEY);
    if (isNil(userDefaultColumns)) {
      return DEFAULT_TABLE_COLUMNS;
    }

    const parsed = jsonParse(userDefaultColumns);
    if (parsed.isErr()) {
      return DEFAULT_TABLE_COLUMNS;
    }

    return isValidColumnArray(parsed.value) ? parsed.value : DEFAULT_TABLE_COLUMNS;
  } catch {
    return DEFAULT_TABLE_COLUMNS;
  }
}

export function setDefaultColumns(columns: TaxOverviewTableColumn[]): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(columns));
  } catch {
    // Silently fail if localStorage is unavailable; user preferences won't be saved but app will continue to work
  }
}

function isValidColumnArray(value: unknown): value is TaxOverviewTableColumn[] {
  return (
    Array.isArray(value) &&
    value.every((item): item is TaxOverviewTableColumn => typeof item === 'string' && ALL_TABLE_COLUMNS_SET.has(item))
  );
}

export function ensureRequiredColumns(columns: readonly TaxOverviewTableColumn[]): TaxOverviewTableColumn[] {
  const result: TaxOverviewTableColumn[] = [...columns];
  const hasClient = result.includes(REQUIRED_COLUMN);

  if (!hasClient) {
    result.unshift(REQUIRED_COLUMN);
  }

  return result;
}

export function getColumnSizing(column: TaxOverviewTableColumn): ColumnSizing {
  switch (column) {
    case 'Client':
      return { flex: '1.5 1 0', minWidth: 200 };
    case 'Filing Type':
      return { flex: '1.5 1 0', minWidth: 175 };
    case 'Tax Year':
      return { flex: '1 1 0', minWidth: 100 };
    case 'Tax Status':
      return { flex: '1.5 1 0', minWidth: 200 };
    case 'Tax Price':
      return { flex: '1 1 0', minWidth: 120 };
    default:
      return defaultGuard(column, { flex: '1 1 0', minWidth: 120 });
  }
}

export function getColumnSortKey(column: TaxOverviewTableColumn): SortBy | undefined {
  switch (column) {
    case 'Client':
      return 'client_name';
    case 'Filing Type':
      return 'filing_type';
    case 'Tax Year':
      return 'tax_year';
    case 'Tax Status':
      return 'tax_status';
    case 'Tax Price':
      return 'price';
    default:
      return undefined;
  }
}
